import * as kiwi from '@lume/kiwi'
import {TFrameBlock} from "./FrameBlockExt";
import {FrameBlockRow} from "./FrameBlockRow";
import {FrameBlockItem} from "./FrameBlockItem";
import {FrameBlockColumn} from "./FrameBlockColumn";
import {FrmVar} from "../../drawfrm/FrmVar";
import {inches} from "../../const";

const DEBUG = false;

// a user-pinned dimension sits just *below* `required`. It still dominates every
// stay/suggestion (so an edited cell forces the frame to resize around it), but
// unlike `required` it can yield when a structural relation would otherwise make
// the layout unsatisfiable. Only the door is truly `required` — its size is
// dictated. Pinning every user edit `required` (as before) left a column of
// user-sized cells beside the door with zero slack: the cells' heights had to
// sum to the door's fixed height *exactly*, and the sub-millimetre float mismatch
// surfaced as an "unsatisfiable constraint" ("Dimensions conflict") on the next
// edit. PIN gives the solver that slack.
const PIN = kiwi.Strength.create(100, 100, 100);

// Sticks consume flow space (the row/column grows by their thickness): interior
// mullions between cells and the block's own edge sticks on the outline alike. Those
// thicknesses are constants here — never solver variables — so they can't change
// while the layout is calculated; they only shift the additive sums. The root block's
// rect is the frame's outer rect.
// a human-readable snapshot of the last solve, surfaced by the debug panel.
// `constraints` are the structural relations fed to the solver (e.g.
// "parent = a + b"). Solved sizes are read live off the model's FrmVars (via the
// frameBlocks debug tree), so they aren't duplicated here.
export type LayoutDebug = {
    constraints: string[];
};

export function recalcLayout(item: TFrameBlock, originX: number, originY: number): LayoutDebug {

    const sol = new kiwi.Solver();
    const debug: LayoutDebug = {constraints: []};

    setupVariables(sol, item);
    setupConstraints(sol, item, debug);

    sol.updateVariables();

    populateResults(item)
    item.reposition(originX, originY);

    return debug;
}

// a dimension is a hard constraint — the solver must honour it, not compute it —
// when the user has pinned it (FrmVar.isModified()) or it is dictated: a door's
// width and height, the threshold's height (thresholdHeight). Everything else is a
// soft suggestion the solver refines, and its solved value is written back below.
function widthIsHard(item: TFrameBlock): boolean {
    return !!item.width?.isModified() || item.model.kind === "doors";
}

function heightIsHard(item: TFrameBlock): boolean {
    return !!item.height?.isModified() || item.model.kind === "doors" || item.model.kind === "threshold";
}

// a dictated dimension is never solved or written back (see widthIsHard/heightIsHard).
function isDictated(item: TFrameBlock, axis: "width" | "height"): boolean {
    if(item.model.kind === "doors") return true;
    return item.model.kind === "threshold" && axis === "height";
}

// how firmly a soft dimension should cling to its current value when the solver
// re-solves. Container blocks (rows/columns, incl. the root frame) hold their size
// (strong) so the overall frame stays put; leaf cells yield (weak) so an edit is
// absorbed by the neighbouring cells rather than growing the whole frame. Both
// still defer to the required structural relations and min/max bounds, so the
// layout stays valid — it just changes as little as possible.
function stayStrength(item: TFrameBlock): number {
    return item instanceof FrameBlockItem ? kiwi.Strength.weak : kiwi.Strength.strong;
}

// how far a pinned dimension may sit from its solved value before we treat the
// pin as having yielded rather than as solver float noise.
const PIN_YIELD_TOLERANCE = 0.001;

function populateResults(item: TFrameBlock) {
    iterateAll(item, item => {
        if(item instanceof FrameBlockRow || item instanceof FrameBlockColumn || item instanceof FrameBlockItem) {
            writeBack(item, item.kiwiWidth, item.width, widthIsHard(item), "width");
            writeBack(item, item.kiwiHeight, item.height, heightIsHard(item), "height");
        }
    })

}

// take one solved size back onto the model. A soft dimension always follows the
// solver. A pinned one usually matches it already, but PIN sits below `required`,
// so a structural relation can override it — e.g. an interior mullion is widened
// and the pinned cells either side no longer sum to their block. Leaving the old
// pinned value there would put the model at odds with the layout that was solved,
// and the cells draw past the block they sit in. Take the solved size instead, and
// drop the pin with it: the size the user asked for isn't reachable any more, so
// the cell goes back to being solver-owned rather than reading as a user dimension
// the layout ignored.
//
// A dictated dimension is the exception: it is not solved (`required`, so it never
// yields), and is never written back.
function writeBack(item: TFrameBlock, kv: kiwi.Variable, fv: FrmVar, hard: boolean, axis: "width" | "height") {
    if(isDictated(item, axis)) return;

    const solved = kv.value();

    if(hard) {
        if(Math.abs(solved - fv.get()) < PIN_YIELD_TOLERANCE) return;

        if(DEBUG) console.log(item.model.id, "pin-yield-" + axis, solved / inches);
        fv.restore(solved, false);
        return;
    }

    if(DEBUG) console.log(item.model.id, "set-" + axis, solved / inches);
    fv.set(solved, {debug: "layout"});
}

function setupVariables(sol: kiwi.Solver, item: TFrameBlock) {
    iterateAll(item, item => {
        if(item instanceof FrameBlockRow || item instanceof FrameBlockColumn || item instanceof FrameBlockItem) {
            item.kiwiWidth = new kiwi.Variable(item.model.id + ":width");
            setupAxis(sol, item, item.kiwiWidth, item.width, widthIsHard(item), "width");

            item.kiwiHeight = new kiwi.Variable(item.model.id + ":height");
            setupAxis(sol, item, item.kiwiHeight, item.height, heightIsHard(item), "height");
        }
    });
}

// pin one axis of an item. A hard dimension is fixed to its current value: a
// dictated one at `required` (immovable), every other pin at PIN (honoured, but
// able to yield rather than crash the solver). A soft dimension is suggested at
// the item's stay strength and clamped to a sane min/max.
function setupAxis(sol: kiwi.Solver, item: TFrameBlock, kv: kiwi.Variable, fv: FrmVar, hard: boolean, axis: "width" | "height") {
    if(hard) {
        const strength = isDictated(item, axis) ? kiwi.Strength.required : PIN;
        if(DEBUG) console.log("layout:", `var(${axis}) ===`, fv.get(), item.model.id)
        sol.addConstraint(new kiwi.Constraint(kv, kiwi.Operator.Eq, fv.get(), strength));
        return;
    }

    if(DEBUG) console.log("layout:", `var(${axis}) ~=`, fv.get(), item.model.id)
    sol.addEditVariable(kv, stayStrength(item));
    sol.suggestValue(kv, fv.get());
    // a void has no minimum (it is a step, any size).
    const min = item.model.kind === "void" ? 0 : 3 * inches;
    sol.addConstraint(new kiwi.Constraint(kv, kiwi.Operator.Ge, min, kiwi.Strength.required));
    sol.addConstraint(new kiwi.Constraint(kv, kiwi.Operator.Le, 12 * 12 * inches, kiwi.Strength.required));
}

function setupConstraints(sol: kiwi.Solver, item: TFrameBlock, debug: LayoutDebug) {
    iterateAll(item, item => {
        if(item instanceof FrameBlockRow) {
            // solve widths (addition). the row is wider than its children by the sum
            // of its stick thicknesses (interior mullions and edge sticks) — a
            // constant, not a variable.
            const innerGap = item.interiorWidthTotal();

            const wItems: (kiwi.Variable | [number, kiwi.Variable] | number)[] = item.children.map(c => c.kiwiWidth);
            wItems.push([-1, item.kiwiWidth]);
            if(innerGap !== 0) wItems.push(innerGap);

            const exp = item.model.id + ":width = " + item.children.map(c => c.model.id + ":width").join(" + ")
                + (innerGap !== 0 ? ` + ${(innerGap / inches).toFixed(3)}"` : "");
            debug.constraints.push(exp);
            if(DEBUG) console.log("layout:", `exp(row-width): `, exp, item.model.id)

            const wExp = new kiwi.Expression(...wItems);
            const wCon = new kiwi.Constraint(wExp, kiwi.Operator.Eq, 0, kiwi.Strength.required);
            sol.addConstraint(wCon);

            // solve heights (equality)
            item.children.forEach(c => {
                const exp = c.model.id + ":height = " + item.model.id + ":height";
                debug.constraints.push(exp);
                if(DEBUG) console.log("layout:", `exp(row-height): `, exp)

                const hExp = new kiwi.Expression(c.kiwiHeight, [-1, item.kiwiHeight]);
                const hCon = new kiwi.Constraint(hExp, kiwi.Operator.Eq, 0, kiwi.Strength.required);
                sol.addConstraint(hCon);
            });
        }

        if(item instanceof FrameBlockColumn) {
            // solve widths (equality)
            item.children.forEach(c => {
                const exp = c.model.id + ":width = " + item.model.id + ":width";
                debug.constraints.push(exp);
                if(DEBUG) console.log("layout:", `exp(col-width): `, exp)

                const wExp = new kiwi.Expression(c.kiwiWidth, [-1, item.kiwiWidth]);
                const wCon = new kiwi.Constraint(wExp, kiwi.Operator.Eq, 0, kiwi.Strength.required);
                sol.addConstraint(wCon);
            });

            // solve heights (addition). like the row width above, the column is
            // taller than its children by the sum of its stick thicknesses.
            const innerGap = item.interiorHeightTotal();

            const hItems: (kiwi.Variable | [number, kiwi.Variable] | number)[] = item.children.map(c => c.kiwiHeight);
            hItems.push([-1, item.kiwiHeight]);
            if(innerGap !== 0) hItems.push(innerGap);

            const exp = item.model.id + ":height = " + item.children.map(c => c.model.id + ":height").join(" + ")
                + (innerGap !== 0 ? ` + ${(innerGap / inches).toFixed(3)}"` : "");
            debug.constraints.push(exp);
            if(DEBUG) console.log("layout:", `exp(col-height): `, exp)

            const hExp = new kiwi.Expression(...hItems);
            const hCon = new kiwi.Constraint(hExp, kiwi.Operator.Eq, 0, kiwi.Strength.required);
            sol.addConstraint(hCon);
        }
    })
}

function iterateAll(item: TFrameBlock, callback: (item: TFrameBlock) => void) {
    let todo: TFrameBlock[] = [item];
    let list: TFrameBlock[] = [item];

    while(todo.length > 0) {
        const next: TFrameBlock[] = [];

        todo.forEach(current => {
            list.push(current);

            if(current instanceof FrameBlockRow || current instanceof FrameBlockColumn) {
                next.push(...current.children);
            }
        });

        todo = next;
    }

    list.reverse().forEach(callback);
}
